import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Navigate, useNavigate, useParams } from 'react-router';
import {
  addField,
  addSection,
  adminTemplateQuery,
  adminTemplatesQuery,
  changeFieldAppliesTo,
  changeFieldType,
  createAdminTemplate,
  moveField,
  moveFieldToSection,
  moveSection,
  parseValidationErrors,
  publishAdminTemplate,
  removeField,
  removeSection,
  saveAdminTemplate,
  structureFromTemplate,
  toTemplateStructure,
  updateField,
  updateSection,
  type EditableTemplate,
  type ParsedValidationErrors,
} from '../lib/adminTemplates';
import { ApiError } from '../lib/api';
import { useMe } from '../lib/auth';
import { pushToast } from '../lib/events';
import { ConfirmRow } from '../components/ConfirmRow';
import { SectionEditor } from '../components/admin/SectionEditor';
import { TemplatePreview } from '../components/admin/TemplatePreview';
import { TemplateStateChip } from '../components/admin/TemplateStateChip';

function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.serverMessage : fallback;
}

function fieldErrorsForSection(
  validationErrors: ParsedValidationErrors | null,
  sectionIndex: number,
): Record<number, Record<string, string>> {
  if (!validationErrors) {
    return {};
  }
  const result: Record<number, Record<string, string>> = {};
  for (const [key, errors] of Object.entries(validationErrors.fields)) {
    const [secIndex, fieldIndex] = key.split(':').map(Number);
    if (secIndex === sectionIndex && fieldIndex !== undefined) {
      result[fieldIndex] = errors;
    }
  }
  return result;
}

/**
 * `/admin/templates/:id`: a draft's editable form (sections/fields, config editors, Save/Publish),
 * or — for a published/retired template — the same layout read-only with a "New version" button
 * instead. Per `docs/specs/WP10-template-editor.md`.
 */
export function AdminTemplateEditor() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { data: me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isAdmin = me?.role === 'ADMIN';
  const { data, isPending, error } = useQuery({ ...adminTemplateQuery(id), enabled: isAdmin && !Number.isNaN(id) });

  const [loadedId, setLoadedId] = useState<number | null>(null);
  const [editable, setEditable] = useState<EditableTemplate | null>(null);
  const [baseline, setBaseline] = useState<ReturnType<typeof toTemplateStructure> | null>(null);
  const [validationErrors, setValidationErrors] = useState<ParsedValidationErrors | null>(null);
  const [publishConfirming, setPublishConfirming] = useState(false);

  // Initialize local editor state once per template id — a background refetch of the same id must
  // never clobber in-progress, unsaved edits. A resync after Save/Publish is done explicitly in
  // those mutations' `onSuccess` instead (see below).
  useEffect(() => {
    if (data && loadedId !== data.id) {
      const structure = structureFromTemplate(data);
      setEditable(structure);
      setBaseline(toTemplateStructure(structure));
      setValidationErrors(null);
      setLoadedId(data.id);
    }
  }, [data, loadedId]);

  const isDirty = editable !== null && baseline !== null && JSON.stringify(toTemplateStructure(editable)) !== JSON.stringify(baseline);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const saveMutation = useMutation({
    mutationFn: saveAdminTemplate,
    onSuccess: (updated) => {
      const structure = structureFromTemplate(updated);
      setEditable(structure);
      setBaseline(toTemplateStructure(structure));
      setValidationErrors(null);
      queryClient.setQueryData(adminTemplateQuery(id).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: adminTemplatesQuery.queryKey });
      pushToast('Saved.', 'success');
    },
    onError: (mutationError) => {
      if (mutationError instanceof ApiError && mutationError.status === 400) {
        setValidationErrors(parseValidationErrors(mutationError.fields));
        pushToast('Fix the highlighted fields and save again.');
        return;
      }
      pushToast(apiErrorMessage(mutationError, 'Could not save the template.'));
    },
  });

  const publishMutation = useMutation({
    mutationFn: publishAdminTemplate,
    onSuccess: (updated) => {
      const structure = structureFromTemplate(updated);
      setEditable(structure);
      setBaseline(toTemplateStructure(structure));
      setValidationErrors(null);
      setPublishConfirming(false);
      queryClient.setQueryData(adminTemplateQuery(id).queryKey, updated);
      void queryClient.invalidateQueries({ queryKey: adminTemplatesQuery.queryKey });
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      pushToast(`Published ${updated.key}@v${updated.version}.`, 'success');
    },
    onError: (mutationError) => {
      setPublishConfirming(false);
      if (mutationError instanceof ApiError && mutationError.status === 400) {
        setValidationErrors(parseValidationErrors(mutationError.fields));
      }
      pushToast(apiErrorMessage(mutationError, 'Could not publish the template.'));
    },
  });

  const newVersionMutation = useMutation({
    mutationFn: createAdminTemplate,
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: adminTemplatesQuery.queryKey });
      navigate(`/admin/templates/${created.id}`);
    },
    onError: (mutationError) => pushToast(apiErrorMessage(mutationError, 'Could not create a new version.')),
  });

  if (!me) {
    return null;
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  if (Number.isNaN(id)) {
    return <Navigate to="/admin/templates" replace />;
  }

  function goBack() {
    if (isDirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    navigate('/admin/templates');
  }

  function update(fn: (template: EditableTemplate) => EditableTemplate) {
    setEditable((prev) => (prev ? fn(prev) : prev));
  }

  /** Reorders go through a view transition so a moved section/field slides from its old place to
   * its new one (the browser's FLIP; each root carries a `view-transition-name`). `flushSync` is the
   * documented pairing: the DOM must be in its final state before the transition's callback
   * returns. Falls back to a plain update without the API or under reduced motion. */
  function updateAnimated(fn: (template: EditableTemplate) => EditableTemplate) {
    const doc = document as Document & { startViewTransition?: (callback: () => void) => unknown };
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof doc.startViewTransition !== 'function' || reduced) {
      update(fn);
      return;
    }
    doc.startViewTransition(() => flushSync(() => update(fn)));
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="reveal flex items-center justify-between gap-4" style={{ ['--i' as string]: 0 }}>
        <button type="button" onClick={goBack} className="eyebrow min-h-tap text-text-faint hover:text-text">
          ← Templates
        </button>
        {isDirty && <span className="eyebrow text-warning">Unsaved changes</span>}
      </div>

      {isPending && <p className="reveal telemetry mt-10 text-text-faint">Loading template…</p>}
      {error && (
        <p role="alert" className="reveal telemetry mt-10 text-signal">
          Could not load this template.
        </p>
      )}

      {data && editable && (
        <>
          <TemplateEditorHeader
            data={data}
            editable={editable}
            isDirty={isDirty}
            validationErrors={validationErrors}
            saving={saveMutation.isPending}
            publishing={publishMutation.isPending}
            creatingVersion={newVersionMutation.isPending}
            publishConfirming={publishConfirming}
            onNameChange={(name) => update((t) => ({ ...t, name }))}
            onSeriesLabelChange={(seriesLabel) => update((t) => ({ ...t, seriesLabel }))}
            onSave={() => saveMutation.mutate({ id, structure: toTemplateStructure(editable) })}
            onRequestPublish={() => setPublishConfirming(true)}
            onCancelPublish={() => setPublishConfirming(false)}
            onConfirmPublish={() => publishMutation.mutate({ id })}
            onNewVersion={() =>
              newVersionMutation.mutate({ key: data.key, name: data.name, seriesLabel: data.seriesLabel, fromTemplateId: data.id })
            }
          />

          <div className="reveal mt-8 flex flex-col gap-5" style={{ ['--i' as string]: 2 }}>
            {editable.sections.map((section, index) => (
              <SectionEditor
                key={section.localId}
                section={section}
                errors={validationErrors?.sections[index] ?? {}}
                fieldErrors={fieldErrorsForSection(validationErrors, index)}
                otherSections={editable.sections
                  .filter((candidate) => candidate.localId !== section.localId)
                  .map((candidate) => ({ localId: candidate.localId, name: candidate.name }))}
                isFirst={index === 0}
                isLast={index === editable.sections.length - 1}
                disabled={data.state !== 'DRAFT'}
                onUpdate={(patch) => update((t) => updateSection(t, section.localId, patch))}
                onMove={(direction) => updateAnimated((t) => moveSection(t, section.localId, direction))}
                onRemove={() => update((t) => removeSection(t, section.localId))}
                onAddField={() => update((t) => addField(t, section.localId))}
                onUpdateField={(fieldLocalId, patch) => update((t) => updateField(t, section.localId, fieldLocalId, patch))}
                onChangeFieldType={(fieldLocalId, type) => update((t) => changeFieldType(t, section.localId, fieldLocalId, type))}
                onChangeFieldAppliesTo={(fieldLocalId, appliesTo) =>
                  update((t) => changeFieldAppliesTo(t, section.localId, fieldLocalId, appliesTo))
                }
                onMoveField={(fieldLocalId, direction) => updateAnimated((t) => moveField(t, section.localId, fieldLocalId, direction))}
                onMoveFieldToSection={(fieldLocalId, toSectionLocalId) =>
                  updateAnimated((t) => moveFieldToSection(t, section.localId, fieldLocalId, toSectionLocalId))
                }
                onRemoveField={(fieldLocalId) => update((t) => removeField(t, section.localId, fieldLocalId))}
              />
            ))}

            {data.state === 'DRAFT' && (
              <button
                type="button"
                onClick={() => update(addSection)}
                className="min-h-tap self-start rounded-md border border-border-strong px-4 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text"
              >
                Add section
              </button>
            )}
          </div>

          <TemplatePreview template={editable} />
        </>
      )}
    </div>
  );
}

interface TemplateEditorHeaderProps {
  data: { key: string; version: number; state: 'DRAFT' | 'PUBLISHED' | 'RETIRED' };
  editable: EditableTemplate;
  isDirty: boolean;
  validationErrors: ParsedValidationErrors | null;
  saving: boolean;
  publishing: boolean;
  creatingVersion: boolean;
  publishConfirming: boolean;
  onNameChange: (name: string) => void;
  onSeriesLabelChange: (seriesLabel: string) => void;
  onSave: () => void;
  onRequestPublish: () => void;
  onCancelPublish: () => void;
  onConfirmPublish: () => void;
  onNewVersion: () => void;
}

function TemplateEditorHeader({
  data,
  editable,
  isDirty,
  validationErrors,
  saving,
  publishing,
  creatingVersion,
  publishConfirming,
  onNameChange,
  onSeriesLabelChange,
  onSave,
  onRequestPublish,
  onCancelPublish,
  onConfirmPublish,
  onNewVersion,
}: TemplateEditorHeaderProps) {
  const isDraft = data.state === 'DRAFT';

  return (
    <div className="reveal mt-4" style={{ ['--i' as string]: 1 }}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="telemetry text-text-faint">
          {data.key}@v{data.version}
        </span>
        <TemplateStateChip state={data.state} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          <span className="eyebrow">Name</span>
          <input
            type="text"
            value={editable.name}
            disabled={!isDraft}
            onChange={(event) => onNameChange(event.target.value)}
            className="display min-h-tap rounded-md border border-border-strong bg-bg px-3 text-2xl text-text disabled:opacity-50"
          />
          {validationErrors?.top.name && (
            <p role="alert" className="telemetry text-signal">
              {validationErrors.top.name}
            </p>
          )}
        </label>
        <label className="flex flex-col gap-1 text-sm text-text-muted">
          <span className="eyebrow">Series label</span>
          <input
            type="text"
            value={editable.seriesLabel}
            disabled={!isDraft}
            onChange={(event) => onSeriesLabelChange(event.target.value)}
            className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text disabled:opacity-50"
          />
          {validationErrors?.top.seriesLabel && (
            <p role="alert" className="telemetry text-signal">
              {validationErrors.top.seriesLabel}
            </p>
          )}
        </label>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {isDraft ? (
          <>
            <button
              type="button"
              disabled={!isDirty || saving}
              onClick={onSave}
              className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-4 text-sm font-semibold text-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>

            {publishConfirming ? (
              <ConfirmRow
                message={
                  data.version > 1
                    ? `Publish v${data.version}? It becomes immutable and retires v${data.version - 1} for new rooms.`
                    : `Publish v${data.version}? It becomes immutable.`
                }
                pending={publishing}
                confirmLabel="Publish"
                onConfirm={onConfirmPublish}
                onCancel={onCancelPublish}
              />
            ) : (
              <button
                type="button"
                disabled={isDirty || publishing}
                onClick={onRequestPublish}
                title={isDirty ? 'Save your changes before publishing.' : undefined}
                className="min-h-tap rounded-md border border-border-strong px-4 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                Publish
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            disabled={creatingVersion}
            onClick={onNewVersion}
            className="min-h-tap rounded-md border border-accent-border bg-accent-dim px-4 text-sm font-semibold text-accent disabled:opacity-50"
          >
            {creatingVersion ? 'Creating…' : 'New version'}
          </button>
        )}
      </div>
    </div>
  );
}
